import axios from 'axios';
import api from '@src/api';
import cloneDeep from 'lodash/cloneDeep';
import store from '@state/store';

export const state = {
  allTrainers: [],
  clientActivity: null,
  selectedTrainer: null,
  trainerDetails: {},
  trainerModel: null,
  selectedProduct: {},
  hasUnsavedFeedbackChanges: false
};

export const getters = {
  hasUnsavedFeedbackChanges: (state) => {
    return state.hasUnsavedFeedbackChanges;
  },
};

export const mutations = {
  setAllTrainers(state, value) {
    state.allTrainers = value;
  },
  setCreateTrainer(state, value) {
    state.allTrainers.push(value);
  },
  setTrainerDetails(state, value) {
    const trainerClone = cloneDeep(state.trainerDetails);
    trainerClone[value._id] = value;
    state.trainerDetails = trainerClone;
  },
  setSelectedTrainer(state, value) {
    state.selectedTrainer = value;
  },
  setClientActivity(state, value) {
    state.clientActivity = value;
  },
  setUpdateTrainer(state, value) {
    const i = state.allTrainers.findIndex(
      (trainer) => trainer._id === value._id
    );
    state.allTrainers[i] = value;
  },
  setUpdateTrainerNotes(state, value) {
    const clonedClientActivity = cloneDeep(state.clientActivity);
    clonedClientActivity.client.trainerNotes = value;
    state.clientActivity = clonedClientActivity;
  },
  setTrainerModel(state, value) {
    state.trainerModel = value;
  },
  setUpdateTrainerProp(state, value) {
    const trainerClone = cloneDeep(state.selectedTrainer);
    trainerClone[value.prop] = value.value;
    state.selectedTrainer = trainerClone;
  },
  setSelectedProduct(state, value) {
    state.selectedProduct = value;
  },
  setUpdateClientCheckInFeedback(state, value) {
    const { checkInId, index, comment } = value;
    const clientActivityClone = cloneDeep(state.clientActivity);
    const checkIn = clientActivityClone.checkIns.find((checkIn) => checkIn._id === checkInId);
    checkIn.comments[index].text = comment;
    state.clientActivity = clientActivityClone
  },
  setHasUnsavedFeedbackChanges(state, value) {
    state.hasUnsavedFeedbackChanges = value;
  }
};

export const actions = {
  getAllTrainers(ctx, { orgId }) {
    if (orgId !== undefined) {
      const url = api.URL_GET_ALL_TRAINERS.replace(':orgID', orgId);

      return axios
        .get(url)
        .then((res) => {
          ctx.commit('setAllTrainers', res.data.trainers);
          return res.data;
        })
        .catch((err) => console.warn(err));
    } else {
      console.error(`invalid params (orgId) : (${orgId})`);
    }
  },

  getTrainer(ctx, { trainerId, orgId }) {
    if (trainerId !== undefined && orgId !== undefined) {
      const url = api.URL_GET_TRAINER.replace(':trainerID', trainerId).replace(
        ':orgID',
        orgId
      );

      return axios
        .get(url)
        .then((res) => {
          ctx.commit('setTrainerDetails', res.data.trainer);
          return res.data;
        })
        .catch((err) => {
          console.warn(err);
        });
    } else {
      console.error(
        `invalid params (trainerId, orgId) : (${trainerId}, ${orgId})`
      );
    }
  },

  getClientActivity(ctx, activityObj) {
    const url = api.URL_GET_CLIENT_ACTIVITY.replace(
      ':trainerID',
      activityObj.trainerId
    )
      .replace(':userID', activityObj.userId)
      .replace(':monday', activityObj.mondaysDate);

    // Minimum data check for obj
    if (
      activityObj.trainerId !== undefined &&
      activityObj.userId !== undefined &&
      activityObj.mondaysDate !== undefined &&
      activityObj.trainerToken !== undefined
    ) {
      const trainerToken = activityObj.trainerToken;
      return axios
        .get(url, null, { headers: { token: `${trainerToken}` } })
        .then((res) => {
          if (res.data.success === false) {
            return res.data.error;
          } else {
            ctx.commit('setClientActivity', res.data);
            return res.data;
          }
        })
        .catch((err) => {
          console.warn(err);
        });
    } else {
      console.error(
        'activityObj must contain the following properties to be a valid replacement [ trainerId, userId, monday ]',
        activityObj
      );
    }
  },

  createTrainer(ctx, { trainerObj }) {
    if (isPayloadValid(trainerObj)) {
      const url = api.URL_CREATE_TRAINER.replace(':orgID', trainerObj.org_id);
      store.commit('auth/SET_AUTH_CHECKED', false);
      // Minimum data check
      if (trainerObj.email !== undefined) {
        const requestPayload = { trainer: trainerObj };

        return axios
          .post(url, requestPayload)
          .then((res) => {
            ctx.commit('setCreateTrainer', res.data.trainer);
            return res.data;
          })
          .catch((err) => {
            console.warn(err);
          });
      } else {
        console.error(
          'trainerObj must contain the following properties "email"',
          trainerObj
        );
      }
    } else {
      logError(trainerObj);
    }
  },

  updateTrainer(ctx, { trainerObj }) {
    if (isPayloadValid(trainerObj)) {
      const url = api.URL_UPDATE_TRAINER.replace(
        ':trainerID',
        trainerObj._id
      ).replace(':orgID', trainerObj.org_id);

      store.commit('auth/SET_AUTH_CHECKED', false);
      // Minimum data check for obj
      if (trainerObj.email !== undefined) {
        const requestPayload = { trainer: trainerObj };
        return axios
          .put(url, requestPayload)
          .then((res) => {
            ctx.commit('setUpdateTrainer', res.data.trainer);
            return res.data;
          })
          .catch((err) => {
            console.warn(err);
          });
      } else {
        console.error(
          'trainerObj must contain the following properties to be a valid replacement [org_id, email]',
          trainerObj
        );
      }
    } else {
      logError(trainerObj);
    }
  },

  updateTrainerNotes(ctx, noteObj) {
    const url = api.URL_UPDATE_CLIENT.replace(
      ':clientID',
      noteObj.clientId
    ).replace(':trainerID', noteObj.trainerId);

    // Minimum data check for obj
    if (
      noteObj.client !== undefined &&
      noteObj.clientId !== undefined &&
      noteObj.trainerId !== undefined
    ) {
      const payload = { client: noteObj.client };
      return axios
        .put(url, payload)
        .then((res) => {
          ctx.commit('setUpdateTrainerNotes', res.data.client.trainerNotes);
          return res.data;
        })
        .catch((err) => {
          console.warn(err);
        });
    } else {
      console.error(
        'noteObj must contain the following properties to be a valid replacement [ client, clientId, trainerId ]',
        noteObj
      );
    }
  },

  getTrainerShape(ctx, { orgId }) {
    if (orgId !== undefined) {
      const url = api.URL_GET_TRAINER_SHAPE.replace(':orgID', orgId);

      return axios
        .get(url)
        .then((res) => {
          ctx.commit('setTrainerModel', res.data.trainer);
          return res.data;
        })
        .catch((err) => {
          console.warn(err);
        });
    } else {
      console.error(`invalid params (orgId) : (${orgId})`);
    }
  },

  createTrainerProductPrice(ctx, { trainerId, priceObj }) {
    const url = api.URL_TRAINER_ADD_PRODUCT_PRICE.replace(
      ':trainerID',
      trainerId
    ).replace(':productID', priceObj.price.productId);
    store.commit('auth/SET_AUTH_CHECKED', false);

    return axios
      .post(url, priceObj)
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        console.warn(err);
      });
  },

  deleteTrainerProductPrice(ctx, { trainerId, productId, priceId }) {
    const url = api.URL_TRAINER_DELETE_PRODUCT_PRICE.replace(
      ':trainerID',
      trainerId
    )
      .replace(':productID', productId)
      .replace(':priceID', priceId);
    store.commit('auth/SET_AUTH_CHECKED', false);

    return axios
      .delete(url)
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        console.warn(err);
      });
  },

  createTrainerCoupon(ctx, { trainerId, couponObj }) {
    const url = api.URL_TRAINER_ADD_COUPON.replace(':trainerID', trainerId);
    store.commit('auth/SET_AUTH_CHECKED', false);

    return axios
      .post(url, couponObj)
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        console.warn(err);
      });
  },

  deleteTrainerCoupon(ctx, { trainerId, couponId }) {
    const url = api.URL_TRAINER_DELETE_COUPON.replace(
      ':trainerID',
      trainerId
    ).replace(':couponID', couponId);
    store.commit('auth/SET_AUTH_CHECKED', false);

    return axios
      .delete(url)
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        console.warn(err);
      });
  },

  cancelTrainerSubscription(ctx, { trainerId, subscriptionId }) {
    const url = api.URL_TRAINER_CANCEL_SUBSCRIPTION.replace(
      ':trainerID',
      trainerId
    ).replace(':subscriptionID', subscriptionId);
    store.commit('auth/SET_AUTH_CHECKED', false);

    return axios
      .delete(url)
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        console.warn(err);
      });
  },

  addTrainerCard(ctx, { trainerId, token }) {
    const url = api.URL_TRAINER_ADD_CARD.replace(':trainerID', trainerId);
    store.commit('auth/SET_AUTH_CHECKED', false);

    return axios
      .post(url, { token })
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        console.warn(err);
      });
  },

  updateTrainerClientFeedback(ctx, comment) {
    const url = api.URL_UPDATE_TRAINER_CLIENT_FEEDBACK
      .replace(':trainerID', comment.trainerId)
      .replace(':clientID', comment.clientId)
      .replace(':checkInID', comment.checkInId)

    // Minimum data check for obj
    if (
      comment.trainerId !== undefined &&
      comment.clientId !== undefined &&
      comment.checkInId !== undefined
    ) {
      const payload = { comment: comment.text };

      return axios
        .post(url, payload)
        .then((res) => {
          return res.data;
        })
        .catch((err) => {
          console.warn(err);
        });
    } else {
      console.error(
        'comment must contain the following properties to be a valid replacement [ trainerId, clientId, checkInId ]',
        comment
      );
    }
  },
};

// Private Helpers
function isPayloadValid(obj) {
  // Minimum data check for payload
  return (
    (obj.org_id !== undefined && obj.email !== undefined) || obj !== undefined
  );
}

function logError(trainerObj) {
  console.error(
    `Invalid Params: (trainerObj, org_id, email) : (${trainerObj}, ${trainerObj.org_id}, ${trainerObj.email})`
  );
}
